import { useEffect } from 'react';

import { format } from 'date-fns';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { tog_chutesideqc } from '@/rtk-query.gql';
import { useUpdateAspirationQcData } from '@/services/aspiration/hooks';
import type { QcDataRequest } from '@/services/aspiration/types';
import { fetchWeather, formatDateWithPattern } from '@/utils';

import { getPumpPressures, getTemps } from '../utils';

import './AspirationQcInformationDialog.css';

type QCInformationDialogProps = {
  setShowQcInformation: (show: boolean) => void;
  qcData: tog_chutesideqc;
  refetchQcData: () => void;
};

export const AspirationQcInformationDialog = ({
  setShowQcInformation,
  qcData,
  refetchQcData,
}: QCInformationDialogProps) => {
  const { control, handleSubmit, reset, setValue } = useForm<tog_chutesideqc>({
    defaultValues: {
      tog_qc_pockettubewarmertemp: '',
      tog_qc_pumptubewarmertemp: '',
      tog_qc_pumppressure: '',
      tog_qc_flowrate: '',
      tog_qc_outsidetemp: '',
      tog_qc_outsideweatherconditions: '',
      tog_qc_collectionroomtemp: '',
      tog_qc_brennerneedlelotno: '',
      aspirationDate: '',
      siteId: '',
      location: '',
      tog_qc_commentchuteside: '',
      chuteNumber: 100000000,
    },
  });
  const [params] = useSearchParams();
  const opuDate =
    params.get('d') || formatDateWithPattern(new Date(), 'MM-dd-yyyy');
  const opuSiteId = params.get('sId') as string;
  const chuteNumber = params.get('chuteNumber') as string;
  const location = params.get('l') as string;

  const {
    mutateAsync: saveAspirationQcData,
    isPending: updateAspirationQcDataLoading,
  } = useUpdateAspirationQcData();

  const temperatureOptions = getTemps();
  const pressureOptions = getPumpPressures();

  useEffect(() => {
    if (qcData) {
      const formData = {
        tog_qc_pockettubewarmertemp: qcData.tog_qc_pockettubewarmertemp || '',
        tog_qc_pumptubewarmertemp: qcData.tog_qc_pumptubewarmertemp || '',
        tog_qc_pumppressure: qcData.tog_qc_pumppressure || '',
        tog_qc_flowrate: qcData.tog_qc_flowrate || '',
        tog_qc_outsidetemp: qcData.tog_qc_outsidetemp || '',
        tog_qc_outsideweatherconditions:
          qcData.tog_qc_outsideweatherconditions || '',
        tog_qc_collectionroomtemp: qcData.tog_qc_collectionroomtemp || '',
        tog_qc_brennerneedlelotno: qcData.tog_qc_brennerneedlelotno || '',
        aspirationDate: opuDate || '',
        siteId: opuSiteId || '',
        location: location || '',
        tog_qc_commentchuteside: qcData.tog_qc_commentchuteside || '',
        chuteNumber: chuteNumber === '2' ? 100000001 : 100000000,
      };

      reset(formData);
    }
  }, [chuteNumber, location, opuDate, opuSiteId, qcData, reset]);

  useEffect(() => {
    const getChutesideWeather = async () => {
      try {
        const data = await fetchWeather();
        setValue('tog_qc_outsidetemp', `${data.currentConditions.temp}C`);
        setValue(
          'tog_qc_outsideweatherconditions',
          data.currentConditions.conditions,
        );
      } catch (error) {
        console.error('Failed to fetch weather data:', error);
      }
    };

    if (
      !qcData?.tog_qc_outsidetemp ||
      !qcData?.tog_qc_outsideweatherconditions
    ) {
      getChutesideWeather();
    }
  }, [qcData, setValue]);

  const onSubmit = async (data: tog_chutesideqc) => {
    const inputData: QcDataRequest = {
      ...data,
      aspirationDate: format(new Date(opuDate), 'yyyy-MM-dd'),
      siteId: opuSiteId as string,
      location: location as string,
      chuteNumber: chuteNumber === '2' ? 100000001 : 100000000,
    };

    await saveAspirationQcData(inputData);
    refetchQcData();
    setShowQcInformation(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="qc-information-dialog__header">
        <strong>
          QC Information must me entered prior to Aspirations. All fields are
          required.
        </strong>
      </div>
      <div className="qc-information-dialog__field">
        <label htmlFor="tog_qc_pockettubewarmertemp">
          Pocket Tube Warmer Temp:
        </label>
        <Controller
          name="tog_qc_pockettubewarmertemp"
          control={control}
          render={({ field }) => (
            <Dropdown
              id="tog_qc_pockettubewarmertemp"
              options={temperatureOptions}
              {...field}
            />
          )}
        />
      </div>

      <div className="qc-information-dialog__field">
        <label htmlFor="tog_qc_pumptubewarmertemp">
          Pump Tube Warmer Temp:
        </label>
        <Controller
          name="tog_qc_pumptubewarmertemp"
          control={control}
          render={({ field }) => (
            <Dropdown
              id="tog_qc_pumptubewarmertemp"
              options={temperatureOptions}
              {...field}
            />
          )}
        />
      </div>

      <div className="qc-information-dialog__field">
        <label htmlFor="tog_qc_pumppressure">Pump Pressure:</label>
        <Controller
          name="tog_qc_pumppressure"
          control={control}
          render={({ field }) => (
            <Dropdown
              id="tog_qc_pumppressure"
              options={pressureOptions}
              {...field}
            />
          )}
        />
      </div>

      <div className="qc-information-dialog__field">
        <label htmlFor="tog_qc_outsidetemp">Outside Temp:</label>
        <Controller
          name="tog_qc_outsidetemp"
          control={control}
          render={({ field }) => (
            <InputText id="tog_qc_outsidetemp" {...field} />
          )}
        />
      </div>

      <div className="qc-information-dialog__field">
        <label htmlFor="tog_qc_outsideweatherconditions">
          Outside Weather Conditions:
        </label>
        <Controller
          name="tog_qc_outsideweatherconditions"
          control={control}
          render={({ field }) => (
            <InputText id="tog_qc_outsideweatherconditions" {...field} />
          )}
        />
      </div>

      <div className="qc-information-dialog__field">
        <label htmlFor="tog_qc_collectionroomtemp">Collection Room Temp:</label>
        <Controller
          name="tog_qc_collectionroomtemp"
          control={control}
          render={({ field }) => (
            <InputText id="tog_qc_collectionroomtemp" {...field} />
          )}
        />
      </div>

      <div className="qc-information-dialog__field">
        <label htmlFor="tog_qc_brennerneedlelotno">Brenner Needle Lot #:</label>
        <Controller
          name="tog_qc_brennerneedlelotno"
          control={control}
          render={({ field }) => (
            <InputText id="tog_qc_brennerneedlelotno" {...field} />
          )}
        />
      </div>

      <div className="qc-information-dialog__field">
        <label htmlFor="tog_qc_commentchuteside">Comments:</label>
        <Controller
          name="tog_qc_commentchuteside"
          control={control}
          render={({ field }) => (
            <InputText id="tog_qc_commentchuteside" {...field} />
          )}
        />
      </div>
      <div className="qc-information-dialog__field">
        <label htmlFor="tog_qc_commentchuteside">Chute Number:</label>
        {chuteNumber || '1'}
      </div>
      <div className="save-buttons">
        <Button
          severity="secondary"
          icon="pi pi-times"
          label="Cancel"
          type="button"
          className="save"
          onClick={() => setShowQcInformation(false)}
        />
        <Button
          icon="pi pi-check"
          label="Save"
          type="submit"
          className="save"
          loading={updateAspirationQcDataLoading}
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </form>
  );
};
